import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, Copy as CopyIcon, Inbox, Link2, LoaderCircle, Plus, RefreshCcwIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  apiErrorMessage,
  approveDropzoneFile,
  getDropzoneLinkUrl,
  listDropzoneLinks,
  listHeldDropzoneFiles,
  rejectDropzoneFile,
  revokeDropzoneLink,
} from "../../../../service/apiService";
import {
  DropzoneLink,
  DropzoneLinkStatus,
  HeldDropzoneFile,
  PASSCODE_LOCKOUT_ACTOR,
} from "../../../../types";
import { useUser } from "../../../../context/UserContext";
import { sameUser } from "../../../../hooks/useResourceLock";
import { formatFileSize } from "../../../../helpers/fileSizeHelper";
import UploadLinkPanel, { absoluteTime, describeRules } from "./UploadLinkPanel";

interface Props {
  brainBank: {
    id: string;
    knowledgebase_name: string;
    created_by?: string;
    locked_by?: string | null;
  };
  /** Called after an approve or reject, so the Files list below can refetch. */
  onChanged?: () => void;
}

const STATUS_PILL: Record<DropzoneLinkStatus, { text: string; className: string }> = {
  active: { text: "Active", className: "bg-violet-50 text-violet-700" },
  expired: { text: "Expired", className: "bg-gray-100 text-gray-600" },
  revoked: { text: "Revoked", className: "bg-amber-50 text-amber-700" },
};

/**
 * The server stamps `status` at fetch time; an Active row whose expires_at has
 * since passed is shown as Expired without a refetch.
 */
export const effectiveStatus = (link: DropzoneLink, now: Date = new Date()): DropzoneLinkStatus => {
  if (link.status === "active" && new Date(link.expires_at).getTime() <= now.getTime()) {
    return "expired";
  }
  return link.status;
};

export const statusPill = (link: DropzoneLink, now: Date = new Date()) => {
  const status = effectiveStatus(link, now);
  if (status === "revoked" && link.revoked_by === PASSCODE_LOCKOUT_ACTOR) {
    return {
      text: "Locked out",
      className: "bg-red-50 text-red-700",
      title: "Revoked automatically after too many wrong passcodes",
    };
  }
  return { ...STATUS_PILL[status], title: undefined as string | undefined };
};

export const scanText = (file: HeldDropzoneFile): string => {
  if (!file.arrived) return "Upload not finished";
  switch (file.scan_status) {
    case "clean":
      return "Scan clean";
    case "flagged":
    case "blocked":
      return "Scan flagged content";
    case "failed":
      return "Scan failed";
    case "pending":
      return "Scanning";
    default:
      return "Not scanned yet";
  }
};

const sizeText = (bytes?: number | null): string => (bytes ? formatFileSize(bytes) : "");

export const expiryText = (link: DropzoneLink, now: Date = new Date()): string => {
  const expires = new Date(link.expires_at);
  if (effectiveStatus(link, now) === "active") {
    const minutes = Math.max(0, Math.ceil((expires.getTime() - now.getTime()) / 60000));
    return minutes <= 1 ? "in under a minute" : `in ${minutes} min`;
  }
  return absoluteTime(link.expires_at);
};

const DropzoneLinks: React.FC<Props> = ({ brainBank, onChanged }) => {
  const { userId, userDetails } = useUser();
  const [links, setLinks] = useState<DropzoneLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [held, setHeld] = useState<HeldDropzoneFile[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);
  const [revokingLinkId, setRevokingLinkId] = useState<string | null>(null);
  const [confirmRejectId, setConfirmRejectId] = useState<string | null>(null);
  const [actingFileId, setActingFileId] = useState<string | null>(null);
  // Re-render once a minute so the "in N min" column moves without a refetch.
  const [, setTick] = useState(0);
  // The BrainBank currently shown; a slow response for a previous one is dropped.
  const shownId = useRef(brainBank.id);
  useEffect(() => {
    shownId.current = brainBank.id;
  }, [brainBank.id]);

  // Who may manage links here, mirroring user_can_access on the API: the
  // owner, an admin, or someone the BrainBank was assigned to. The API
  // enforces it independently; this only decides what to render and fetch.
  const me = userId || userDetails?.id;
  const isAdmin = userDetails?.role === "admin";
  const granted = (userDetails?.assigned_knowledge_base || []).includes(brainBank.id);
  const isOwner = sameUser(brainBank.created_by, me);
  // Who a lock stops, mirroring is_locked_for(): the lock holder, the owner
  // and anyone the BrainBank was assigned to stay through.
  const lockedForMe =
    Boolean(brainBank.locked_by) && !sameUser(brainBank.locked_by, me) && !isOwner && !granted;
  const canManage = (isAdmin || isOwner || granted) && !lockedForMe;

  const fetchLinks = useCallback(async () => {
    const id = brainBank.id;
    setLoading(true);
    try {
      const data = await listDropzoneLinks(id);
      if (shownId.current === id) setLinks(data);
    } catch (error) {
      if (shownId.current === id) toast.error(apiErrorMessage(error, "Failed to load upload links"));
    } finally {
      if (shownId.current === id) setLoading(false);
    }
  }, [brainBank.id]);

  const fetchHeld = useCallback(async () => {
    const id = brainBank.id;
    try {
      const data = await listHeldDropzoneFiles(id);
      if (shownId.current === id) setHeld(data);
    } catch (error) {
      if (shownId.current === id) toast.error(apiErrorMessage(error, "Failed to load files awaiting approval"));
    }
  }, [brainBank.id]);

  useEffect(() => {
    if (!canManage) return;
    fetchLinks();
    fetchHeld();
  }, [canManage, fetchLinks, fetchHeld]);

  const handleApprove = async (file: HeldDropzoneFile) => {
    setActingFileId(file.id);
    try {
      await approveDropzoneFile(file.id);
      setHeld((current) => current.filter((f) => f.id !== file.id));
      toast.success(`"${file.file_name}" approved and sent for processing`);
      onChanged?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not approve the file"));
    } finally {
      setActingFileId(null);
    }
  };

  const handleReject = async (file: HeldDropzoneFile) => {
    setActingFileId(file.id);
    try {
      await rejectDropzoneFile(file.id);
      setHeld((current) => current.filter((f) => f.id !== file.id));
      toast.success(`"${file.file_name}" rejected and deleted`);
      onChanged?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not reject the file"));
    } finally {
      setActingFileId(null);
      setConfirmRejectId(null);
    }
  };

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const [copyingLinkId, setCopyingLinkId] = useState<string | null>(null);

  const handleCopy = async (link: DropzoneLink) => {
    setCopyingLinkId(link.link_id);
    try {
      const url = await getDropzoneLinkUrl(link.link_id);
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not copy the link"));
    } finally {
      setCopyingLinkId(null);
    }
  };

  const handleRevoke = async (linkId: string) => {
    setRevokingLinkId(linkId);
    try {
      const updated = await revokeDropzoneLink(linkId);
      setLinks((current) => current.map((l) => (l.link_id === linkId ? updated : l)));
      toast.success("Upload link revoked");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to revoke the upload link"));
    } finally {
      setRevokingLinkId(null);
      setConfirmRevokeId(null);
    }
  };

  if (!canManage) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
            <Link2 className="h-4 w-4 text-gray-500" />
            Upload links
          </h4>
          <p className="text-xs text-gray-500 mt-1">
            Send a link to a client so they can add files to this BrainBank without a Kyva account.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            aria-expanded={showCreate}
            disabled={showCreate}
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 whitespace-nowrap disabled:opacity-50"
          >
            <Plus size={16} />
            Create upload link
          </button>
        )}
      </div>

      {/* In place, never a second dialog: the BrainBanks screen is already one. */}
      {showCreate && (
        <UploadLinkPanel
          brainBank={{ id: brainBank.id, name: brainBank.knowledgebase_name }}
          onCreated={() => {
            fetchLinks();
            fetchHeld();
          }}
          onClose={() => setShowCreate(false)}
        />
      )}

      {held.length > 0 && (
        <section className="border border-amber-200 bg-amber-50 rounded-lg p-3" aria-label="Awaiting approval">
          <h5 className="text-sm font-medium text-amber-900 flex items-center gap-1.5">
            <Inbox className="h-4 w-4" />
            Awaiting approval ({held.length})
          </h5>
          <p className="text-xs text-amber-800 mt-1">
            These files arrived through a link set to hold for approval. Nothing is processed until you approve it.
          </p>
          <ul className="mt-3 divide-y divide-amber-200">
            {held.map((file) => (
              <li key={file.id} className="py-2 flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{file.file_name}</p>
                  <p className="text-xs text-gray-600">
                    {[file.dropzone_label ? `via ${file.dropzone_label}` : null, sizeText(file.file_size), scanText(file)]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
                {canManage && (
                  <div className="flex items-center gap-2 text-sm">
                    {confirmRejectId === file.id ? (
                      <span className="inline-flex items-center gap-2 text-xs text-gray-700">
                        Delete this file?
                        <button
                          type="button"
                          disabled={actingFileId === file.id}
                          onClick={() => handleReject(file)}
                          className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                        >
                          {actingFileId === file.id && <LoaderCircle size={12} className="animate-spin" />}
                          Yes
                        </button>
                        <button
                          type="button"
                          disabled={actingFileId === file.id}
                          onClick={() => setConfirmRejectId(null)}
                          className="text-gray-500 hover:text-gray-700 font-medium disabled:opacity-50"
                        >
                          No
                        </button>
                      </span>
                    ) : (
                      <>
                        <button
                          type="button"
                          disabled={!file.arrived || actingFileId === file.id}
                          onClick={() => handleApprove(file)}
                          title={file.arrived ? "Approve and process" : "The upload has not finished"}
                          className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50"
                        >
                          {actingFileId === file.id ? (
                            <LoaderCircle size={12} className="animate-spin" />
                          ) : (
                            <Check size={12} />
                          )}
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={actingFileId === file.id}
                          onClick={() => setConfirmRejectId(file.id)}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <Trash2 size={12} />
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Files issued</th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center">
                  <RefreshCcwIcon className="animate-spin h-4 w-4 text-gray-400 inline-block" />
                </td>
              </tr>
            ) : links.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-sm text-gray-500">
                  No upload links yet
                </td>
              </tr>
            ) : (
              links.map((link) => {
                const status = effectiveStatus(link);
                const pill = statusPill(link);
                return (
                  <tr key={link.link_id} className="hover:bg-gray-50">
                    <td className="px-3 py-3">
                      <div className="text-sm font-medium text-gray-900">{link.label}</div>
                      <div className="text-xs text-gray-500">{describeRules(link)}</div>
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">{expiryText(link)}</td>
                    <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {link.files_issued} / {link.max_files}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${pill.className}`} title={pill.title}>
                        {pill.text}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      {status === "active" && canManage && link.url_available && confirmRevokeId !== link.link_id && (
                        <button
                          type="button"
                          disabled={copyingLinkId === link.link_id}
                          onClick={() => handleCopy(link)}
                          className="inline-flex items-center gap-1 mr-4 text-sm text-violet-700 hover:text-violet-800 font-medium disabled:opacity-50"
                        >
                          {copyingLinkId === link.link_id ? (
                            <LoaderCircle size={14} className="animate-spin" />
                          ) : (
                            <CopyIcon size={14} />
                          )}
                          Copy link
                        </button>
                      )}
                      {status === "active" && canManage && (
                        confirmRevokeId === link.link_id ? (
                          <span className="inline-flex items-center gap-2 text-xs text-gray-700">
                            Revoke?
                            <button
                              type="button"
                              disabled={revokingLinkId === link.link_id}
                              onClick={() => handleRevoke(link.link_id)}
                              className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                            >
                              {revokingLinkId === link.link_id && (
                                <LoaderCircle size={12} className="animate-spin" />
                              )}
                              Yes
                            </button>
                            <button
                              type="button"
                              disabled={revokingLinkId === link.link_id}
                              onClick={() => setConfirmRevokeId(null)}
                              className="text-gray-500 hover:text-gray-700 font-medium disabled:opacity-50"
                            >
                              No
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmRevokeId(link.link_id)}
                            className="text-sm text-red-600 hover:text-red-700 font-medium"
                          >
                            Revoke
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default DropzoneLinks;
